import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { SYSTEMS_CATALOG } from '../../types/systems';

interface AdoptionRow {
  system_name:           string;
  unique_users:          number;
  total_sessions:        number;
  avg_sessions_per_user: number;
  last_active:           string | null;
}

function Kpi({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="border border-[#C9A84C]/20 p-5 rounded-sm bg-[#0D1130]/40">
      <div className="text-[#F5ECD7]/50 text-xs uppercase tracking-widest mb-1">{label}</div>
      <div className="text-[#C9A84C] text-3xl font-light">{value}</div>
      {sub && <div className="text-[#F5ECD7]/40 text-xs mt-1">{sub}</div>}
    </div>
  );
}

export function SystemAdoptionPage() {
  const [rows,    setRows]    = useState<AdoptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    supabase
      .from('system_adoption_metrics')
      .select('*')
      .then(({ data, error: e }) => {
        if (e) setError(e.message);
        else   setRows((data ?? []) as AdoptionRow[]);
        setLoading(false);
      });
  }, []);

  const totalSessions  = rows.reduce((s, r) => s + r.total_sessions, 0);
  const totalUsers     = rows.reduce((max, r) => Math.max(max, r.unique_users), 0);
  const adoptedSystems = rows.filter(r => r.unique_users > 0).length;

  function getLabel(key: string) {
    return SYSTEMS_CATALOG.find(s => s.key === key)?.label ?? key;
  }

  return (
    <div className="p-8 text-[#F5ECD7]">
      <div className="mb-8">
        <h1 className="text-[#C9A84C] text-2xl font-light tracking-wider mb-1">
          Adoção de Sistemas
        </h1>
        <p className="text-[#F5ECD7]/50 text-sm">Feature discovery, quantos usuários usam cada sistema</p>
      </div>

      {loading && <div className="text-[#F5ECD7]/50 text-sm">Carregando…</div>}
      {error   && <div className="mb-4 p-4 border border-red-500/40 bg-red-900/20 text-red-300 text-sm rounded-sm">{error}</div>}

      {!loading && !error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <Kpi label="Sessões totais"     value={totalSessions.toLocaleString('pt-BR')} />
            <Kpi label="Max usuários únicos" value={totalUsers.toLocaleString('pt-BR')} />
            <Kpi label="Sistemas ativos"    value={`${adoptedSystems}/16`} />
            <Kpi label="Média de sistemas"  value={
              totalUsers > 0
                ? (rows.reduce((s, r) => s + r.unique_users, 0) / totalUsers / rows.length * rows.length).toFixed(1)
                : '-'
            } sub="por usuário ativo" />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-[#C9A84C]/20">
                  {['Sistema', 'Usuários únicos', 'Sessões totais', 'Média/usuário', 'Último acesso'].map(h => (
                    <th key={h} className="pb-3 pr-6 text-[#F5ECD7]/40 text-xs uppercase tracking-wider font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const sys  = SYSTEMS_CATALOG.find(s => s.key === r.system_name);
                  const pct  = totalUsers > 0 ? Math.round((r.unique_users / totalUsers) * 100) : 0;
                  return (
                    <tr key={r.system_name} className="border-b border-[#C9A84C]/10 hover:bg-[#C9A84C]/5">
                      <td className="py-3 pr-6">
                        <div className="flex items-center gap-2">
                          <span>{sys?.icon ?? '✦'}</span>
                          <span className="text-[#F5ECD7]/80">{getLabel(r.system_name)}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-6">
                        <div className="flex items-center gap-3">
                          <span className="text-[#C9A84C]">{r.unique_users.toLocaleString('pt-BR')}</span>
                          <div className="flex-1 h-1.5 bg-[#C9A84C]/10 rounded-full overflow-hidden max-w-[80px]">
                            <div className="h-full bg-[#C9A84C] rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[#F5ECD7]/40 text-xs">{pct}%</span>
                        </div>
                      </td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/70">{r.total_sessions.toLocaleString('pt-BR')}</td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/70">{r.avg_sessions_per_user?.toFixed(1) ?? '-'}</td>
                      <td className="py-3 pr-6 text-[#F5ECD7]/50 text-xs">
                        {r.last_active ? new Date(r.last_active).toLocaleDateString('pt-BR') : '-'}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-[#F5ECD7]/30 text-center">Sem dados ainda</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
